/**
 * Text Utilities Library - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive text processing utility library with advanced features:
 * - HTML content processing and sanitization
 * - Date formatting and parsing utilities
 * - Text cleaning and transformation functions
 * - URL and link processing utilities
 * - Content normalization and validation
 * - React component integration with HTML parsing
 * - Meta tag and title processing
 * - Responsive text truncation and formatting
 * 
 * Core Features:
 * - HTML tag stripping and content extraction
 * - Date format conversion and validation
 * - Text truncation with smart ellipsis
 * - Meta tag removal and content cleaning
 * - URL pattern matching and removal
 * - Link conversion and target attribute management
 * - Title removal from HTML content
 * - Text normalization and case conversion
 * 
 * Date Processing:
 * - Multiple date format support (DD/MM/YYYY, YYYY-MM-DD, ISO)
 * - Date validation and error handling
 * - Irish locale formatting
 * - Quarter calculation from dates
 * - Month/year extraction utilities
 * 
 * Content Processing:
 * - HTML to plain text conversion
 * - Content cleaning for display
 * - Bold tag identification and removal
 * - Empty paragraph cleanup
 * - Link processing and conversion
 * 
 * Text Operations:
 * - Word counting in HTML content
 * - Text normalization (uppercase to title case)
 * - Response formatting for AI content
 * - File name extraction from paths
 * - CSS class name processing
 * 
 * @requires html-react-parser For HTML to React component conversion
 * @returns {Object} Comprehensive text processing utilities
 */
import Parser from 'html-react-parser';

// Strip HTML tags and decode HTML entities
export const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Truncate text to specified length and add ellipsis
export const truncateText = (text, maxLength = 600) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Remove meta tags in brackets
export const removeMetaTags = (text) => {
  if (!text) return '';
  // Match any text in square brackets, including nested brackets
  return text.replace(/\[[^\]]*\]/g, '').trim();
};

// Process text: strip HTML, remove meta tags, and truncate
export const processText = (text, maxLength = 600) => {
  if (!text) return '';
  // First strip HTML tags
  const strippedHtml = stripHtml(text);
  // Then remove meta tags
  const cleanedText = removeMetaTags(strippedHtml);
  // Finally truncate
  return truncateText(cleanedText, maxLength);
};

export const getMonthYear = (dateInput) => {
    // Reuse the formatDate function to parse the input date
    const formattedDate = formatDate(dateInput);

    // Check if formatDate returned a valid date
    if (formattedDate === 'Invalid Date' || formattedDate === 'No Date Available') {
        return formattedDate;
    }

    // Extract day, month, and year from the formatted date (DD/MM/YYYY)
    const [ , month, year ] = formattedDate.split('/');

    // Array of month names
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Convert the month number to the corresponding month name
    const monthName = monthNames[parseInt(month, 10) - 1];

    // Return the formatted Month Name/Year
    return `${monthName} ${year}`;
};

// Helper function to format dates
export const formatDate = (dateInput) => {
    
    // Check if the input is already a Date object
    let date;
    if (dateInput instanceof Date && !isNaN(dateInput)) {
        date = dateInput;
    } else if (typeof dateInput === 'string') {
        if (dateInput.includes('T')) {
            // Handle ISO format (e.g., 2020-01-04T08:00:00Z)
            const [year, month, day] = dateInput.split('T')[0].split('-').map(Number);
            date = new Date(year, month - 1, day);
        } else if (dateInput.includes('-')) {
            // Handle YYYY-MM-DD format
            const [year, month, day] = dateInput.split('-').map(Number);
            date = new Date(year, month - 1, day);
        } else {
            // Handle DD/MM/YYYY or MM/DD/YYYY
            const [first, second, third] = dateInput.split('/').map(Number);

            if (first > 12) {
                // Interpret as DD/MM/YYYY if day > 12
                date = new Date(third, second - 1, first);
            } else if (second > 12) {
                // Interpret as MM/DD/YYYY if month > 12
                date = new Date(third, first - 1, second);
            } else {
                // Default to DD/MM/YYYY for ambiguous cases
                date = new Date(third, second - 1, first);
            }
        }
    } else {
        return 'No Date Available';
    }

    // Check if the parsed date is valid
    if (!isNaN(date.getTime())) {
        // Format to DD/MM/YYYY
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } else {
        return 'Invalid Date';
    }
};

export const formatDateSimple = (dateString) => {
    try {
        // Handle DD/MM/YYYY format
        if (dateString.includes('/')) {
            const [day, month, year] = dateString.split('/').map(Number);
            const date = new Date(year, month - 1, day);
            
            // Check for invalid date
            if (isNaN(date.getTime())) {
                console.error("Invalid date format:", dateString);
                return;
            }

            // Format the date for display (e.g., "October 18, 2024") 
            const options = { year: 'numeric', month: 'long', day: 'numeric' };
            return date.toLocaleDateString('en-IE', options);
        }

        // Handle other date formats
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            console.error("Invalid date format:", dateString);
            return;
        }

        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-IE', options);

    } catch (error) {
        console.error("Error handling date:", error);
    }
};

export const newDate = (dateString) => {
    const date = new Date(dateString);
    return date;
}

export const removeTime = (dateString) => {
    return dateString ? dateString.split(' ')[0] : 'N/A';
}

// Utility function to parse a date in dd/mm/yyyy format
export const parseDate = (dateString) => {
    // Check if dateString is already a Date object
    if (dateString instanceof Date) {
        return dateString;
    }

    // Check if dateString is a string
    if (typeof dateString !== 'string') {
        return null;
    }

    // Split the string to extract day, month, and year
    const [day, month, year] = dateString.split('/').map(Number);

    // Validate extracted values
    if (!day || !month || !year) {
        return null;
    }

    // Create and return a new Date object
    return new Date(year, month - 1, day); // Month is 0-indexed in JavaScript
};

export const getQuarterFromDate = (date) => {
    if (!date) return null;
    const month = date.getMonth();
    if (month >= 0 && month <= 2) return 'Q1';
    if (month >= 3 && month <= 5) return 'Q2';
    if (month >= 6 && month <= 8) return 'Q3';
    if (month >= 9 && month <= 11) return 'Q4';
    return null;
};

export const removeURLs = (text) => {
    if(text == undefined){
        return "";
    }
    // Regular expression to match URLs and surrounding brackets
    const urlPattern = /\[?https?:\/\/[^\s]+\]?/g;
    return text.replace(urlPattern, '').trim();
};

/**
 * Decodes Microsoft Dynamics 365 tracking URLs to extract the actual target URL
 * @param {string} url - The potentially encoded tracking URL
 * @returns {string} - The decoded target URL or original URL if not a tracking URL
 */
export const decodeDynamicsTrackingUrl = (url) => {
    try {
        // Check if it's a Microsoft Dynamics tracking URL
        if (url.includes('mkt.dynamics.com') || url.includes('dynamics.com')) {
            const urlObj = new URL(url);
            const targetParam = urlObj.searchParams.get('target');
            
            if (targetParam) {
                // Decode the target parameter (it's URL encoded JSON)
                const decodedTarget = decodeURIComponent(targetParam);
                const targetJson = JSON.parse(decodedTarget);
                
                if (targetJson.TargetUrl) {
                    // The TargetUrl is double-encoded, so decode it twice
                    const firstDecode = decodeURIComponent(targetJson.TargetUrl);
                    const finalUrl = decodeURIComponent(firstDecode);
                    
                    // Clean up any remaining tracking parameters
                    const cleanUrl = finalUrl.split('?')[0];
                    return cleanUrl;
                }
            }
        }
        
        // If not a tracking URL or couldn't decode, return original
        return url;
    } catch (error) {
        console.warn('Error decoding Dynamics tracking URL:', error);
        return url;
    }
};

/**
 * Cleans and processes URLs in text content
 * @param {string} text - Text that may contain URLs
 * @returns {string} - Text with cleaned URLs
 */
export const cleanAndDecodeUrls = (text) => {
    if (typeof text !== 'string') {
        return text;
    }

    // Pattern to match URLs
    const urlPattern = /(https?:\/\/[^\s\[\]<>'"]+)/g;
    
    return text.replace(urlPattern, (match) => {
        // Decode tracking URLs
        const cleanedUrl = decodeDynamicsTrackingUrl(match);
        
        // If it's still a very long URL (>100 chars), truncate for display
        if (cleanedUrl.length > 100) {
            const urlObj = new URL(cleanedUrl);
            return `${urlObj.protocol}//${urlObj.hostname}...`;
        }
        
        return cleanedUrl;
    });
};

export const cleanLinks = (text) => {
    // Check if text is defined and is a string
    if (typeof text !== 'string') {
        // Return text as it is if it's undefined or not a string
        return text;
    }

    // Instead of removing URLs completely, let's clean and decode them
    return cleanAndDecodeUrls(text);
};


export const convertLastWordToLink = (text) => {
    // Regular expression to match the last word before the URL in brackets
    const urlRegex = /(\w+)\s*\[([^\]]+)\]/g;
    // Replace the last word before the URL with an HTML anchor tag using the URL
    return text.replace(urlRegex, (match, wordBeforeUrl, url) => {
        // Return the hyperlink with the word before and the URL
        return `<a href="${url}" target="_blank">${wordBeforeUrl}</a>`;
    });
};

export const getLastSpecificDay = (dayOfWeek) => {
    const today = new Date();
    const currentDayOfWeek = today.getDay();
    const daysDifference = (currentDayOfWeek + 7 - dayOfWeek) % 7;
    const lastSpecificDay = new Date(today);
    lastSpecificDay.setDate(today.getDate() - daysDifference);
    lastSpecificDay.setHours(0, 0, 0, 0);
    return lastSpecificDay;
};

export const getToday = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set time to the end of today
    return today;
};

export const replaceCss = (text) => {
    // Safety check for undefined/null values
    if (!text || typeof text !== 'string') {
        return '';
    }
    return text.replace(/&/g, "").replace(/\s+/g, "");
};

export function addTargetBlankToLinks(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    // Find all anchor tags in the parsed HTML
    const anchorTags = doc.querySelectorAll('a');
    
    anchorTags.forEach((anchor, index) => {
        // Get the current href
        const currentHref = anchor.getAttribute('href');
        
        if (currentHref) {
            // Decode Microsoft Dynamics tracking URLs
            const cleanedHref = decodeDynamicsTrackingUrl(currentHref);
            anchor.setAttribute('href', cleanedHref);
        }
        
        // Add target="_blank" to each anchor tag
        anchor.setAttribute('target', '_blank');
        // (Optional) Add rel="noopener noreferrer" for security reasons
        anchor.setAttribute('rel', 'noopener noreferrer');
    });
    
    return doc.body.innerHTML;
}

export const cleanJson = (text) => {
    // Check if text is defined and is a string
    if (typeof text !== 'string') {
        // Return text as is if it's undefined or not a string
        return text;
    }
    // Perform the replacement
    return text.replace(/\\u0027/g, "'");
};

export function extractFileName(filePath) {
    // Remove any fragment identifiers (e.g., #zoom=75) if they exist
    const cleanPath = filePath.split('#')[0];
    // Split by '/' to get the last part, which should be the file name
    const fileName = cleanPath.split('/').pop();
    return fileName;
}

export function removeTitle(htmlString, title) {
    // return Parser(htmlString);
    // Escape special characters in the title for safe usage in regex
    const escapedTitle = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Create a regex to match the title at the beginning of the content, even if there are HTML tags between words
    const titlePattern = escapedTitle.split(' ').map(word => `(?:<[^>]+>)*${word}`).join('(\\s|(?:<[^>]+>))*');
    const regex = new RegExp(`^${titlePattern}`, 'i');  // 'i' flag for case-insensitive matching
    // Check if the HTML string starts with the title (handling tags and case insensitivity) and remove it
    const cleanedHtml = htmlString.replace(regex, '').trim();
    // Return the cleaned HTML parsed into React components
    return Parser(cleanedHtml);

}

export const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
};

export const countWords = (html) => {
    // Remove HTML tags and convert HTML entities
    const text = html.replace(/<[^>]*>/g, ' ')
                    .replace(/&nbsp;/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();
    return text ? text.split(/\s+/).length : 0;
};

export function formatIrishDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

export function removeFirstBoldTag(htmlString, title) {
   
    // Helper function to normalize apostrophes and quotes
    const normalizeText = (text) => {
        return text
            .replace(/[''`´]/g, "'")  // Normalize different apostrophe types
            .replace(/\u2019/g, "'") // Unicode right single quotation mark (8217)
            .replace(/[""]/g, '"')   // Normalize different quote types
            .replace(/\u0027/g, "'") // Unicode apostrophe
            .replace(/&#39;/g, "'")  // HTML entity apostrophe
            .replace(/&apos;/g, "'") // XML apostrophe entity
            .toLowerCase()
            .trim();
    };
    
    // Check if title exists and has content
    if (title) {
        // Split title into words and take the first few (e.g., first 3 words for comparison)
        const titleWords = normalizeText(title.split(" ").slice(0, 3).join(" "));
        
        // Create a temporary DOM element to manipulate the HTML content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;
        
        // Instead of checking if content starts with title, look for bold elements containing the title

        // Find all bold elements
        const boldElements = tempDiv.querySelectorAll('b, strong, span[style*="font-weight: bold"], span[style*="font-weight:bold"], strong[style], p strong, p[style*="font-weight: bold"]');
        
        let titleFound = false;
        
        boldElements.forEach((boldElement, index) => {
            
            // Check if this bold element contains the title text
            const boldText = normalizeText(boldElement.textContent || '');
            const boldWords = boldText.split(" ").slice(0, 3).join(" ");
            
            // Check if this bold element contains the title
            const isMatch = boldText.includes(titleWords) || 
                           titleWords.includes(boldText) ||
                           boldWords === titleWords;
                    
            if (isMatch && !titleFound) {
                titleFound = true;
 
                
                // Find the appropriate parent to remove
                let elementToRemove = boldElement;
             
                
                // If strong is inside a p tag, consider removing the p tag
                if (boldElement.tagName === 'STRONG' && boldElement.parentElement && boldElement.parentElement.tagName === 'P') {
                    const pElement = boldElement.parentElement;

                    // Check if the p tag only contains this strong element (and maybe whitespace)
                    const pContent = pElement.innerHTML.replace(boldElement.outerHTML, '').trim();
                    
                    if (pContent === '' || pContent.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                        elementToRemove = pElement;
                    }
                }
                
                // If the element to remove is inside a div that only contains this element, remove the div instead
                if (elementToRemove.parentElement && elementToRemove.parentElement.tagName === 'DIV') {
                    const divElement = elementToRemove.parentElement;
                    
                    const divContent = divElement.innerHTML.replace(elementToRemove.outerHTML, '').trim();
                    
                    if (divContent === '' || divContent.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                        elementToRemove = divElement;
                    }
                }

                elementToRemove.remove();

            }
        });
        
        
        // Clean up ALL empty p tags throughout the content (not just after title removal)
        const allEmptyParagraphs = tempDiv.querySelectorAll('p');
        allEmptyParagraphs.forEach(p => {
            const content = p.innerHTML.trim();
            // Remove p tags that only contain br tags, &nbsp;, whitespace, or are completely empty
            if (content === '<br>' || content === '<br/>' || content === '<br />' || 
                content === '&nbsp;' || content === '' || content === ' ' ||
                content === '&nbsp;&nbsp;' || content.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                p.remove();
            }
        });

        // Clean up empty div tags as well
        const allEmptyDivs = tempDiv.querySelectorAll('div');
        allEmptyDivs.forEach(div => {
            const content = div.innerHTML.trim();
            if (content === '<br>' || content === '<br/>' || content === '<br />' || 
                content === '&nbsp;' || content === '' || content === ' ' ||
                content === '&nbsp;&nbsp;' || content.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                div.remove();
            }
        }); 

        // Return the cleaned HTML as React components
        return Parser(tempDiv.innerHTML);
    } else {
        
        // If no title exists, still clean up empty paragraphs and return
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;
        
        // Clean up empty p tags even when no title removal is needed
        const allEmptyParagraphs = tempDiv.querySelectorAll('p');
        allEmptyParagraphs.forEach(p => {
            const content = p.innerHTML.trim();
            if (content === '<br>' || content === '<br/>' || content === '<br />' || 
                content === '&nbsp;' || content === '' || content === ' ' ||
                content === '&nbsp;&nbsp;' || content.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                p.remove();
            }
        });
        
        return Parser(tempDiv.innerHTML);
    }
}

export function normalizeText(input) {
    if (!input) return ''; 
    if (input === 'N/A') return 'N/A'; // Single check for 'N/A'

    // Check if the input is all in uppercase
    if (input === input.toUpperCase()) {
        // Convert to lowercase and capitalize the first letter of each word
        return input.toLowerCase().replace(/(?:^|\s)\S/g, (match) => match.toUpperCase());
    }

    // If not all caps, return the input unchanged
    return input;
}

export const formatResponse = (response) => {
    if (typeof response !== 'string') {
        console.error('Expected a string but received:', response);
        return "Error: Response is not valid.";
    }

    const formattedResponse = response
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n\n/g, '<br><br>')
        .replace(/\n/g, '<br>');

    return `<p>${formattedResponse}</p>`;
};
export function removeTitleFromPlainText(description, title) {
    // Normalize both strings (make them lowercase and remove extra spaces)
    const normalizedTitle = title.toLowerCase().trim();
    const normalizedDescription = description.toLowerCase().trim();
    
    // Check if the description starts with the title
    if (normalizedDescription.startsWith(normalizedTitle)) {
        // Remove the title from the description (case-sensitive version for keeping original format)
        return description.slice(title.length).trim();
    }
    
    // If no match, return the original description
    return description;
}

/**
 * Cleans messy HTML while preserving important formatting like lists, bold text, etc.
 * Removes unwanted elements, inline styles, and classes while keeping structure
 * 
 * @param {string} html - Raw HTML content (potentially messy)
 * @param {number} maxLength - Maximum character length for truncation
 * @returns {string} Cleaned HTML with preserved formatting
 */
export const cleanAndTruncateHtml = (html, maxLength = 280) => {
  if (!html) return '';
  
  // First, decode HTML entities to make the content readable
  let cleanedHtml = html
    .replace(/&#58;/g, ':')
    .replace(/&#160;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ');
  
  // Create a temporary DOM element to parse and clean the HTML
  const temp = document.createElement('div');
  temp.innerHTML = cleanedHtml;
  
  // Remove only the most problematic elements (but keep content)
  const unwantedElements = temp.querySelectorAll('script, style, meta, link, title');
  unwantedElements.forEach(el => el.remove());
  
  // Remove elements with ExternalClass (these are usually Outlook junk)
  const externalClassElements = temp.querySelectorAll('[class*="ExternalClass"]');
  externalClassElements.forEach(el => {
    // Keep the content but remove the element wrapper
    while (el.firstChild) {
      el.parentNode.insertBefore(el.firstChild, el);
    }
    el.remove();
  });
  
  // Remove inline styles and problematic attributes but keep the elements
  const allElements = temp.querySelectorAll('*');
  allElements.forEach(element => {
    element.removeAttribute('style');
    element.removeAttribute('class');
    element.removeAttribute('id');
    
    // Keep href for links, but remove other attributes
    if (element.tagName.toLowerCase() !== 'a') {
      const attributesToRemove = ['font-family', 'font-size', 'color', 'margin', 'padding'];
      attributesToRemove.forEach(attr => {
        element.removeAttribute(attr);
      });
    }
  });
  
  // Get the cleaned HTML
  cleanedHtml = temp.innerHTML;
  
  // Clean up excessive whitespace
  cleanedHtml = cleanedHtml
    .replace(/\s+/g, ' ')
    .replace(/>\s+</g, '><')
    .trim();
  
  // Simple truncation - just cut at character limit if needed
  if (cleanedHtml.length > maxLength) {
    // Find a safe place to cut (try to avoid cutting in the middle of a tag)
    let cutPoint = maxLength;
    
    // Look for the last complete word before the limit
    const textBeforeLimit = cleanedHtml.substring(0, maxLength);
    const lastSpaceIndex = textBeforeLimit.lastIndexOf(' ');
    
    if (lastSpaceIndex > maxLength * 0.8) {
      cutPoint = lastSpaceIndex;
    }
    
    cleanedHtml = cleanedHtml.substring(0, cutPoint) + '...';
  }
  
  return cleanedHtml;
};

